import {createInMemoryCache} from '@algolia/cache-in-memory';
import {Box, Button, Link} from '@mui/material';
// import {useSearchBox} from 'react-instantsearch-hooks-web';
import {
  Configure,
  Highlight,
  Hits,
  InstantSearch,
  useInstantSearch,
  useHits,
} from 'react-instantsearch-hooks-web';
import {handleConvertToLocaleContentful} from '~/utils';
import algoliasearch from 'algoliasearch/lite';
import Typography from '@wearesection/bandai-react-components/atoms/Typography';
import dynamic from 'next/dynamic';
import {usePageSetting} from '~/providers/page-context.provider';
import {useEffect, useMemo, useState} from 'react';
import InsightsMiddleware from './insightsMiddleware';

const searchInsights = require('search-insights');
searchInsights('init', {
  appId: process.env.NEXT_PUBLIC_ALGOLIA_APPP_ID,
  apiKey: process.env.NEXT_PUBLIC_ALGOLIA_API_KEY,
  useCookie: true,
});

const ConnectedSearchBox = dynamic(() => import('./searchBoxWidget'), {
  ssr: false,
});
type IInstantSearchCustom = {
  handleCloseSearchForm: Function;
  handleShowHit: Function;
  isShowSearch: boolean;
  handleShowTrending: Function;
  getValueSearch: Function;
  dataSubMenuMemo: any;
  locale: string;
  showHits: boolean;
  // listResultDefaultState: any;
  valueSearch: string;
  dataTrending?: any;
};

const InstantSearchCustom = (props: IInstantSearchCustom) => {
  const {
    handleCloseSearchForm,
    handleShowHit,
    isShowSearch,
    handleShowTrending,
    getValueSearch,
    dataSubMenuMemo,
    locale,
    showHits,
    // listResultDefaultState,
    valueSearch,
    dataTrending,
  } = props;

  const algoliaClient = algoliasearch(
    process.env.NEXT_PUBLIC_ALGOLIA_APPP_ID as string,
    process.env.NEXT_PUBLIC_ALGOLIA_API_KEY as string
  );

  const searchClient = {
    ...algoliaClient,
    responsesCache: createInMemoryCache(), // or createNullCache(),
    requestsCache: createInMemoryCache({serializable: false}),
    clickAnalytics: true,
    search(requests: any) {
      if (
        requests.every(
          ({params}: {params: any}) =>
            params.query?.length > (locale.toLocaleLowerCase() === 'sea' ? 2 : 0)
        )
      ) {
        return algoliaClient.search(requests);
      } else {
        return Promise.resolve({
          results: requests.map(() => ({
            hits: [],
            nbHits: 0,
            nbPages: 0,
            page: 0,
            processingTimeMS: 0,
          })),
        });
      }
    },
  };

  const {globalData} = usePageSetting();
  // const dataTranslation = globalData?.dataTranslation?.[0] ?? {};
  const [dataRecent, setDataRecent] = useState<string[]>([]);
  const [showRecent, setShowRecent] = useState<boolean>(true);
  const [dataTrendingState, setDataTrendingState] = useState<any>(dataTrending);

  function NoResultsBoundary({children, fallback}: {children: any; fallback: any}) {
    const {results} = useInstantSearch();

    // The `__isArtificial` flag makes sure to not display the No Results message
    // when no hits have been returned yet.
    if (!results.__isArtificial && results.nbHits === 0) {
      return (
        <>
          {fallback}
          <div hidden>{children}</div>
        </>
      );
    }

    return children;
  }

  useEffect(() => {
    setDataTrendingState(dataTrending);
  }, [dataTrending]);

  useEffect(() => {
    const recentInLocal = localStorage.getItem('recent_local');
    if (recentInLocal) {
      const recentLocal = JSON.parse(recentInLocal) ?? [];
      const dataClone = [...recentLocal];
      const dataSplit = dataClone.splice(recentLocal.length > 5 ? recentLocal.length - 5 : 0);
      setShowRecent(true);
      setDataRecent(dataSplit.reverse());
    } else {
      setShowRecent(false);
    }
  }, []);

  const topTrendingAndCacheMemo = useMemo(
    () => (
      <Box
        sx={{
          width: '100%',
          maxWidth: {
            xl: '636px',
            lg: '463px',
            md: '286px',
            xs: '100%',
          },
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <Box
          sx={{
            padding: {
              xs: '0 46px',
              sm: '0 54px',
              md: '0',
            },
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: 'row',
            },
          }}
        >
          <Box
            sx={{
              maxWidth: '274px',
              width: '100%',
            }}
          >
            <Typography
              variant="p7"
              color={'#9F9F9F'}
              sx={{
                marginBottom: '8px',
              }}
              textTransform={undefined}
            >
              {dataSubMenuMemo?.propsSearch?.textTopTrending ?? 'Top Trending'}
            </Typography>

            <Box>
              {!!dataTrendingState.length &&
                dataTrendingState.map((item: any, idx: number) => (
                  <Typography
                    variant="p5"
                    textTransform={undefined}
                    key={idx}
                    sx={{
                      '& a': {
                        color: '#151515',
                        textDecoration: 'none',
                      },
                      marginBottom: '16px',
                      // width: 'fit-content',
                      // position: 'relative',
                      // overflow: 'hidden',
                      // textOverflow: 'ellipsis',
                      // display: '-webkit-box',
                      // lineClamp: '1',
                      // WebkitLineClamp: 1,
                      // WebkitBoxOrient: 'vertical',

                      // height: '26px',
                      '&::after': {
                        content: "' '",
                        position: 'absolute',
                        left: '0px',
                        bottom: '0px',
                        width: '0%',
                        transition: 'all 0.3s',
                        borderBottom: '1px solid #9F9F9F',
                      },
                      '&:hover': {
                        '&::after': {
                          width: '100%',
                        },
                      },
                    }}
                  >
                    <Link href={`/${locale}/search?q=${item?.title}`}>{item.title}</Link>
                  </Typography>
                ))}
            </Box>
          </Box>
          {!!dataRecent.length && showRecent && (
            <Box
              sx={{
                maxWidth: '274px',
                width: '100%',
                marginLeft: {
                  xs: '0px',
                  md: '32px',
                },
                marginTop: {
                  xs: ' 40px',
                  md: '0px',
                },
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: {
                    xs: 'calc(100% - 46px)',
                    md: '100%',
                  },
                  top: '0px',
                }}
              >
                <Typography
                  variant="p5"
                  color="#737373"
                  sx={{
                    whiteSpace: 'nowrap',
                    position: 'relative',
                    cursor: 'pointer',
                    width: 'fit-content',
                    marginBottom: {
                      xs: '16px',
                      ld: '0px',
                    },

                    '&::before': {
                      content: '" "',
                      position: 'absolute',
                      left: '0px',
                      bottom: '0px',
                      width: '100%',
                      borderBottom: '1px solid #737373',
                    },
                  }}
                  textTransform={undefined}
                  onClick={() => {
                    localStorage.removeItem('recent_local');
                    setShowRecent(false);
                  }}
                >
                  {dataSubMenuMemo?.propsSearch?.textClearAll ?? 'Clear all'}
                </Typography>
              </Box>
              <Typography
                variant="p7"
                color={'#9F9F9F'}
                sx={{
                  marginBottom: '8px',
                }}
                textTransform={undefined}
              >
                {dataSubMenuMemo?.propsSearch?.textRecent ?? 'Recent'}
              </Typography>

              <Box>
                {!!dataRecent.length &&
                  dataRecent.map((item: any, idx: number) => (
                    <Typography
                      variant="p5"
                      textTransform={undefined}
                      key={idx}
                      sx={{
                        '& a': {
                          color: '#151515',
                          textDecoration: 'none',
                        },
                        marginBottom: '16px',
                        width: 'fit-content',
                        position: 'relative',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        lineClamp: '1',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',

                        height: '26px',
                        '&::after': {
                          content: "' '",
                          position: 'absolute',
                          left: '0px',
                          bottom: '0px',
                          width: '0%',
                          transition: 'all 0.3s',
                          borderBottom: '1px solid #9F9F9F',
                        },
                        '&:hover': {
                          '&::after': {
                            width: '100%',
                          },
                        },
                      }}
                    >
                      <Link href={`/${locale}/search?q=${item}`}>{item}</Link>
                    </Typography>
                  ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    ),
    [dataRecent, showRecent, dataTrendingState]
  );

  function NoResults() {
    // const {indexUiState} = useInstantSearch();
    // return (
    //   <Box
    //     sx={{
    //       color: '#151515',
    //       fontWeight: '400',
    //       textAlign: 'center',
    //     }}
    //   >
    //     No results for{' '}
    //     <Box
    //       component={'q'}
    //       sx={{
    //         display: 'inline-block',
    //         fontWeight: '600',
    //       }}
    //     >
    //       {indexUiState.query}
    //     </Box>
    //   </Box>
    // );

    return <Box>{topTrendingAndCacheMemo}</Box>;
  }

  const Hit = (props: any, locale: string) => {
    return (
      <Box
        className="hit-title"
        sx={{
          '& .ais-Highlight-highlighted': {
            color: '#151515',
            fontWeight: '700',
            backgroundColor: 'transparent',
          },
          marginBottom: '16px',
          '&:hover': {
            '& p': {
              '&::after': {
                width: '100%',
              },
            },
          },
        }}
      >
        <Typography
          variant="p5"
          textTransform={undefined}
          sx={{
            '& a': {
              fontWeight: '400',
              color: '#151515',
            },
            width: 'fit-content',
            position: 'relative',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            lineClamp: '1',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',

            height: '26px',
            '&::after': {
              content: "' '",
              position: 'absolute',
              left: '0px',
              bottom: '0px',
              width: '0%',
              transition: 'all 0.3s',
              borderBottom: '1px solid #9F9F9F',
            },
          }}
        >
          <Link
            onClick={async () => {
              await props.sendEvent('click', props.hit, 'Result Search On Header');
              window.location.href = `/${locale}/search?q=${props.hit.title}`;
            }}
            sx={{
              cursor: 'pointer',
            }}
          >
            <Highlight attribute="title" hit={props.hit} />
          </Link>
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <InstantSearch
        searchClient={searchClient as any}
        indexName={handleConvertToLocaleContentful(locale as string)}
        stalledSearchDelay={1000}
      >
        <InsightsMiddleware />
        <ConnectedSearchBox
          onClose={() => handleCloseSearchForm()}
          handleShowHit={handleShowHit}
          isShowSearch={isShowSearch}
          handleShowTrending={handleShowTrending}
          getValueSearch={getValueSearch}
          locale={locale}
          propsSearch={{
            ...dataSubMenuMemo.propsSearch,
            listResultDefault: [],
          }}
        />
        <Configure
          hitsPerPage={5}
          facets={['title', 'slug']}
          analytics
          clickAnalytics
          facetFilters={[['type:Games']]}
        />
        {/* Top Trending */}
        {!showHits && <Box>{topTrendingAndCacheMemo}</Box>}

        {/* Top suggestion */}
        {showHits && (
          <NoResultsBoundary fallback={<NoResults />}>
            <Box
              sx={{
                width: '100%',
                maxWidth: {
                  xl: '636px',
                  lg: '463px',
                  md: '286px',
                  xs: '100%',
                },
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <Box
                sx={{
                  '& .ais-Hits-list': {
                    textDecoration: 'none',
                    padding: '0px',
                  },
                  '& a': {
                    textDecoration: 'none',
                  },
                  '& li': {
                    listStyle: 'none !important',
                  },
                  padding: {
                    xs: '0 46px',
                    sm: '0 54px',
                    md: '0',
                  },
                }}
              >
                <Typography
                  variant="p7"
                  color={'#9F9F9F'}
                  sx={{
                    marginBottom: '8px',
                  }}
                  textTransform={undefined}
                >
                  {dataSubMenuMemo?.propsSearch?.textTopSuggestion ?? 'Top suggestions'}
                </Typography>

                <Hits hitComponent={props => Hit(props, locale)} />
                {/* <Hits hitComponent={Hit} /> */}
                <Link href={`/${locale}/search?q=${valueSearch}`} sx={{}}>
                  <Typography
                    variant="p5"
                    color="#737373"
                    sx={{
                      position: 'relative',
                      cursor: 'pointer',
                      width: 'fit-content',
                      marginBottom: {
                        xs: '16px',
                        ld: '0px',
                      },

                      '&::before': {
                        content: '" "',
                        position: 'absolute',
                        left: '0px',
                        bottom: '0px',
                        width: '100%',
                        borderBottom: '1px solid #737373',
                      },
                    }}
                    textTransform={undefined}
                  >
                    {dataSubMenuMemo?.propsSearch?.textSeeAllResults ?? ' See all results'}
                  </Typography>
                </Link>
              </Box>
            </Box>
          </NoResultsBoundary>
        )}
      </InstantSearch>
    </Box>
  );
};

export default InstantSearchCustom;
